import React from 'react';
import { Field, Formik, FormikHelpers as FormikActions, useFormikContext } from 'formik';
import {
  CheckboxField,
  Form,
  FormColumn,
  FormControls,
  FormRow,
  FormTitle,
  InputField,
  SelectField,
  IntentText,
} from '~/components/Form';
import { Button } from '~/components/Button';
import { IModalComponent, IModalComponentClose } from '~/components/Modal';
import { Intent } from '~/lib/intent';
import { IPlacement, PlacementSchema } from '~/modules/Sources/models/Placement';
import { UsdInputField } from '~/modules/BusinessIncentives/components/UsdInputField';
import style from '~/modules/Sources/forms/index.module.scss';

export const platforms = [
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

export const adTypes = [
  { value: 'native', label: 'Native' },
  { value: 'banner', label: 'Banner' },
  { value: 'rewarded', label: 'Rewarded' },
  { value: 'interstitial', label: 'Interstitial' },
];

export const placementTypes = [
  { value: 'bidding', label: 'Bidding' },
  { value: 'waterfall', label: 'Waterfall' },
];

const HvaDefaultSync: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<IPlacement>();
  const prevPlacementType = React.useRef(values.placementType);
  const prevHva = React.useRef(values.hva);
  const hvaManuallyChanged = React.useRef(false);
  const applyingDefault = React.useRef(false);

  React.useEffect(() => {
    if (values.hva !== prevHva.current) {
      if (!applyingDefault.current) {
        hvaManuallyChanged.current = true;
      }
      applyingDefault.current = false;
      prevHva.current = values.hva;
    }
  }, [values.hva]);

  React.useEffect(() => {
    if (values.placementType !== prevPlacementType.current) {
      prevPlacementType.current = values.placementType;
      if (!hvaManuallyChanged.current) {
        applyingDefault.current = true;
        setFieldValue('hva', values.placementType === 'bidding');
      }
      if (values.placementType === 'bidding') {
        setFieldValue('pricefloor', null);
      }
    }
  }, [values.placementType, setFieldValue]);

  return null;
};

interface IPlacementForm {
  onSubmit: (
    values: IPlacement,
    formikActions: FormikActions<IPlacement>,
    close: IModalComponentClose
  ) => void;
}

export const PlacementForm: IModalComponent<IPlacementForm> = (props) => (
  <Formik
    initialValues={props.placement}
    validationSchema={PlacementSchema}
    onSubmit={(placement, actions) => {
      const cleaned = {
        ...placement,
        pricefloor: placement.placementType === 'waterfall' ? placement.pricefloor : null,
        gpid: placement.gpid?.trim() || null,
        maxAdUnitId: placement.maxAdUnitId?.trim() || null,
      };
      props.onSubmit(cleaned, actions, props.close);
    }}
  >
    {({ values: placement, status, isSubmitting }) => (
      <Form>
        <HvaDefaultSync />
        <FormTitle>{placement.id ? 'Edit Placement' : 'Create Placement'}</FormTitle>

        {status && <IntentText intent={Intent.DANGER}>{status}</IntentText>}

        <FormRow>
          <FormColumn>

            <Field
              name="name"
              label="Name"
              component={InputField}
              options={{
                placeholder: 'Placement Name',
                disabled: isSubmitting,
              }}
            />

            <Field
              name="platform"
              label="Platform"
              component={SelectField}
              options={{
                placeholder: 'Select Platform',
                items: platforms,
                disabled: isSubmitting,
              }}
            />

            <Field
              name="bundle"
              label="Bundle"
              component={InputField}
              options={{
                placeholder: 'com.example.app',
                disabled: isSubmitting,
              }}
            />

            <Field
              name="adType"
              label="Ad Type"
              component={SelectField}
              options={{
                placeholder: 'Select Ad Type',
                items: adTypes,
                disabled: isSubmitting,
              }}
            />

            <Field
              name="placementType"
              label="Placement Type"
              component={SelectField}
              options={{
                placeholder: 'Select Placement Type',
                items: placementTypes,
                disabled: isSubmitting,
              }}
            />

            <div className={style.hvaField}>
              <Field
                name="hva"
                label="HVA"
                component={CheckboxField}
                options={{
                  disabled: isSubmitting,
                }}
              />
              <span className={style.hvaHint}>
                Select if this placement is targeted for High Value Users
              </span>
            </div>

            <Field
              name="maxAdUnitId"
              label="MAX Ad Unit ID"
              component={InputField}
              options={{
                placeholder: '16 hex characters, from MAX',
                disabled: isSubmitting,
              }}
            />

            <Field
              name="gpid"
              label="GPID override"
              component={InputField}
              options={{
                placeholder: 'Optional: sent to all buyers instead of the mediation ID',
                disabled: isSubmitting,
              }}
            />

            {placement.placementType === 'waterfall' && (
              <Field
                name="pricefloor"
                label="Pricefloor (USD)"
                component={UsdInputField}
                options={{
                  placeholder: '0.00',
                  disabled: isSubmitting,
                }}
              />
            )}
          </FormColumn>
        </FormRow>

        <FormControls>
          <Button onClick={props.close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            intent={Intent.PRIMARY}
            disabled={isSubmitting}
            rightIcon={isSubmitting && <i className="fa fa-spinner fa-spin" />}
          >
            Save
          </Button>
        </FormControls>
      </Form>
    )}
  </Formik>
);
